import ScriptWorker from '../../../../../../shared/util/ScriptWorker';

/**
 * Establishes a messaging protocol
 * inputs: (via postMessage)
 *   {
 *     method: {'loadImage'|'cancelImage'}, - method to execute
 *     position: {x,y} - used to sync requests and responses
 *     url: {string} - url to request
 *   }
 * outputs: (via onMessage)
 *   {
 *     method: {'loadImage'|'cancelImage'}, - method that completed
 *     position: {x,y} - from input
 *     result: {Any}
 *     error: {Error} - supplied if an error
 *   }
 */

/**
 * Worker script to load images.
 * Note: This script is executed in a separate process and doesn't have access to resources on the main thread. Avoid
 *       using 'import'ed modules, async and other "new" features to prevent webpack from importing external dependencies
 *       as these will cause this script to fail.
 */
function imageLoader () {
  const requests = new Map();

  function loadImage (position, url) {
    const controller = new AbortController();
    requests.set(position, controller);

    // Retrieve the image
    fetch(url, controller)
      // Blob the image
      .then((response) => response.blob())
      // Return it to the main thread
      .then((blob) => {
        respond('loadImage', position, blob);
      })
      // Notify of unexpected errors
      .catch((ex) => {
        if (ex.name !== 'AbortError') {
          respond('loadImage', position, null, ex);
        }
      })
      .finally(() => {
        requests.delete(position);
      });
  }

  function cancelImage (position) {
    const isRequesting = requests.has(position);
    if (isRequesting) {
      requests.get(position).abort();
    }
    respond('cancelImage', position, isRequesting);
  }

  function respond (method, position, result, error) {
    const message = {
      method,
      position,
      result
    };
    if (error) {
      message.error = error;
    }
    self.postMessage(message);
  }

  const messageRouter = {
    loadImage,
    cancelImage
  };

  self.addEventListener('message', (event) => {
    const { method, position, url } = event.data;

    if (messageRouter) {
      messageRouter[method](position, url);
    }
  });
}

export class ImageLoader extends ScriptWorker {
  constructor () {
    super(imageLoader);
  }
}
